// first-match-wins：設計前提是一個落地網址只會對應一個廣告商，正常情況不會有兩筆廠商 click id
//   同時出現在同一組 query string 裡，因此陣列順序不是「優先權」機制，新增廠商直接接在陣列尾端即可。
export const VENDOR_PARAMS = [
  { param: 'ttclid', vendor: 'TIKTOK' },
  { param: 'fbclid', vendor: 'META' },
  { param: 'pixel_click_id', vendor: 'GEEMEE' },
  { param: '3s_clickid', vendor: '3SNET' },
  { param: 'ts_clickid', vendor: 'TRAFFICSTARS' },
  { param: 'mgsky_clickid', vendor: 'MGSKYADS' },
  { param: 'bidv_clickid', vendor: 'BIDVERTISER' },
  // PropellerAds 用 ?cost= 帶廣告成本（非通用 ?price=）；priceParam 標記命中 PROPELLER 時 price 改讀 ?cost=，
  //   通用 ?price= 對它視為無關參數（非 fallback），避免跨廠商歸因污染。
  { param: 'propeller_clickid', vendor: 'PROPELLER', priceParam: 'cost' },
  { param: 'mr_click_id', vendor: 'MEETROAS' },
]

export const detectVendorClick = (searchParams) => {
  for (const { param, vendor, priceParam } of VENDOR_PARAMS) {
    const traceId = searchParams.get(param)
    // priceParam 只在該廠商有定義時才附加，維持其他廠商 { traceId, vendorCode } 的既有回傳形狀
    if (traceId) return priceParam ? { traceId, vendorCode: vendor, priceParam } : { traceId, vendorCode: vendor }
  }
  return null
}

// 未定義 priceParam 的廠商（以及沒命中任何廠商時）一律讀這個通用 key
const DEFAULT_PRICE_PARAM = 'price'

export const SENSITIVE_QUERY_KEYS = new Set(['token', 'otp', 'phone', 'code', 'password', 'auth', 'access_token', 'x', 'pwd'])

export const stripAuthParams = (href) => {
  try {
    const url = new URL(href)
    const sensitiveKeys = [...url.searchParams.keys()].filter((key) => SENSITIVE_QUERY_KEYS.has(key.toLowerCase()))
    if (sensitiveKeys.length === 0 && !url.hash) return href

    sensitiveKeys.forEach((key) => url.searchParams.delete(key))
    url.hash = ''
    return url.toString()
  } catch {
    return href
  }
}

// 導頁時必須剝除的 query（TI-15642 原則 7）：授權類參數不得留在導頁後的網址列；
// 其餘（ac / fc / utm_* / fbclid / modal…）一律保留，否則廣告落地打錯路徑就整組遺失。
// ⚠️ `error` 刻意不剝：JT-1784716920 REQ-6 以「query 帶非空 error」當作不開窗的守門條件，
//    且 layouts/default.vue 會讀它 setError 後自行導回首頁清掉，剝掉會讓錯誤靜默消失、彈窗照開。
export const REDIRECT_STRIPPED_QUERY_KEYS = new Set(SENSITIVE_QUERY_KEYS)

export const stripSensitiveQuery = (query) =>
  Object.fromEntries(Object.entries(query || {}).filter(([key]) => !REDIRECT_STRIPPED_QUERY_KEYS.has(String(key).toLowerCase())))

export const DEFAULT_AC = 'officialdefault'

export const resolveAc = ({ attribution, cookieAc, authed, agentId }) => {
  const attributedAc = attribution ? attribution.ac : cookieAc
  return attributedAc || (authed ? agentId : undefined) || DEFAULT_AC
}

export const resolveActualAgentId = ({ authed, agentId, ac }) => {
  if (!authed || agentId === undefined || agentId === null) return undefined
  return String(agentId) !== String(ac) ? String(agentId) : undefined
}

export const EXTRA_KEYS = new Set([
  'value',
  'currency',
  'transaction_id',
  'isFirst',
  'paymentMethod',
  'activityName',
  'activityNo',
  'location',
  'query',
  'searchTag',
  'method',
])

export const buildExtra = (payload) => {
  const extra = {}
  if (!payload) return extra
  for (const key of EXTRA_KEYS) {
    if (payload[key] !== undefined) extra[key] = payload[key]
  }
  return extra
}

export const buildConversionBody = ({
  traceId,
  vendorCode,
  capturedAt,
  eventTime,
  eventId,
  uniqId,
  event,
  ac,
  actualAgentId,
  price,
  externalId,
  site,
  fbc,
  fbp,
  ttp,
  eventSourceUrl,
  extra,
  utm,
}) => ({
  traceId,
  vendorCode,
  capturedAt,
  eventTime,
  eventId,
  uniqId,
  event,
  ac,
  actualAgentId,
  price,
  externalId,
  site,
  fbc,
  fbp,
  ttp,
  eventSourceUrl,
  extra,
  utm,
})

const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000

// plugins/09.umami.client.js 共用同一份（原本兩處各一份副本會漂移）
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id']

// 從當前網址解析 UTM（純函數，吃 search 或完整 href / fullPath 字串）。TI-15642：UTM 讀取端補「回頭讀網址」那一層。
// 只留 UTM_KEYS 的非空值；完全沒有 utm_* 時回 undefined，讓呼叫端能用
// `parseUrlUtm(...) || parseStoredUtm(...)` 退回 localStorage 的 last-touch 值。
// ⚠️ 命中即**整包取代**，不與 stored 逐 key 合併——否則會拼出「新 source ＋ 上一輪 campaign」這種從未存在過的組合。
export const parseUrlUtm = (input) => {
  if (!input || typeof input !== 'string') return undefined
  // 先剝 #fragment，否則最後一個 query value 會被污染成 `facebook#promo`
  const text = input.split('#')[0]
  const index = text.indexOf('?')
  const params = new URLSearchParams(index >= 0 ? text.slice(index + 1) : text)
  const out = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value != null && value !== '') out[key] = String(value)
  }
  return Object.keys(out).length ? out : undefined
}

export const parseStoredUtm = (raw, nowMs = Date.now()) => {
  if (!raw) return undefined
  try {
    const { utm, ts } = JSON.parse(raw)
    if (!utm || typeof ts !== 'number') return undefined
    if (nowMs - ts > UTM_TTL_MS) return undefined
    return utm
  } catch {
    return undefined
  }
}

export const parseAttribution = (search) => {
  const withoutHash = String(search ?? '').split('#')[0]
  const queryIndex = withoutHash.indexOf('?')
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : withoutHash
  const params = new URLSearchParams(query)
  const click = detectVendorClick(params)
  // price 讀取哪個 query key 完全由命中的廠商決定，不是「兩個都讀、取其一」的 fallback：
  //   - 廠商有自訂 priceParam（如 PropellerAds 的 cost）→ 只讀那一個，通用 ?price= 對它視為無關參數
  //   - 廠商沒有自訂（或沒命中任何廠商）→ 讀通用 ?price=
  const priceParam = click?.priceParam || DEFAULT_PRICE_PARAM
  return {
    traceId: click?.traceId,
    vendorCode: click?.vendorCode,
    ac: params.get('ac') || undefined,
    price: params.get(priceParam) || undefined,
  }
}
